import * as IF from "infrontjs";
import template from "./template.html";

// Registers the custom elements used inside the shell template.
import "../sidebar-nav";
import "../user-menu";

const COLLAPSE_KEY = "belturo.sidebar.collapsed";

/**
 * <app-layout> — the SaaS Admin shell.
 * Renders the collapsible sidebar + topbar + page-content slot when a user is
 * logged in, or an empty host (for the auth screens) when not. State decides
 * which, via app.user. Re-render with .render() after the auth state changes.
 */
class AppLayout extends HTMLElement
{
    constructor()
    {
        super();
        this._connected = false;
        this._docBound = false;
    }

    connectedCallback()
    {
        if ( false === this._connected )
        {
            this.app = IF.App.get();
            this.render();
            this._connected = true;
        }
    }

    render()
    {
        this.app.view.render( this, template, { user: this.app.user } );

        if ( this.app.user )
        {
            this.bindShell();
        }
    }

    bindShell()
    {
        this.admin = this.querySelector( '[data-id="admin"]' );

        const rail      = this.querySelector( '[data-id="rail-toggle"]' );
        const hamburger = this.querySelector( '[data-id="hamburger"]' );
        const scrim     = this.querySelector( '[data-id="scrim"]' );

        // Restore the persisted desktop collapsed state.
        if ( localStorage.getItem( COLLAPSE_KEY ) === "1" )
        {
            this.admin.classList.add( "admin--collapsed" );
        }
        this.setRailIcon( rail );

        // Desktop: collapse to an icon rail (persisted).
        rail?.addEventListener( "click", () =>
        {
            this.admin.classList.toggle( "admin--collapsed" );
            localStorage.setItem( COLLAPSE_KEY, this.admin.classList.contains( "admin--collapsed" ) ? "1" : "0" );
            this.setRailIcon( rail );
        } );

        // Mobile: off-canvas drawer.
        hamburger?.addEventListener( "click", () => this.admin.classList.add( "admin--drawer" ) );
        scrim?.addEventListener( "click", () => this.admin.classList.remove( "admin--drawer" ) );

        // Nav items: set active + update the topbar title (real routing comes later).
        this.querySelectorAll( ".nav-item" ).forEach( ( item ) =>
        {
            item.addEventListener( "click", ( e ) =>
            {
                e.preventDefault();
                this.querySelectorAll( ".nav-item" ).forEach( ( n ) => n.classList.remove( "nav-item--active" ) );
                item.classList.add( "nav-item--active" );

                const title = this.querySelector( ".topbar__title" );
                const sub = this.querySelector( ".topbar__sub" );
                if ( title ) title.textContent = item.querySelector( ".nav-item__label" ).textContent;
                if ( sub ) sub.textContent = "";

                if ( this.isMobile() ) this.admin.classList.remove( "admin--drawer" );

                // Items with a data-route navigate to that state (the topbar
                // heading is then refreshed from getContentHeaderData).
                const route = item.getAttribute( "data-route" );
                if ( route ) this.app.router.redirect( route );
            } );
        } );

        // Document-level listener bound once: Escape closes the mobile drawer.
        // (The user menu owns its own outside-click/Escape handling.)
        if ( false === this._docBound )
        {
            document.addEventListener( "keydown", ( e ) =>
            {
                if ( e.key === "Escape" )
                {
                    this.admin?.classList.remove( "admin--drawer" );
                }
            } );

            this._docBound = true;
        }
    }

    setRailIcon( rail )
    {
        if ( !rail || !this.admin ) return;
        const collapsed = this.admin.classList.contains( "admin--collapsed" );
        const icon = rail.querySelector( ".icon" );
        if ( icon )
        {
            icon.classList.toggle( "icon--panel-left-open", collapsed );
            icon.classList.toggle( "icon--panel-left-close", !collapsed );
        }
    }

    isMobile()
    {
        return window.matchMedia( "(max-width: 860px)" ).matches;
    }
}

customElements.define( "app-layout", AppLayout );
